import { calcDistance } from './walk';

const pathLen = (start, list) => {
  let len = 0;
  let prev = start;
  list.forEach((s) => {
    if (prev) len += calcDistance(prev[0], prev[1], s.latitude, s.longitude);
    prev = [s.latitude, s.longitude];
  });
  return len;
};

/**
 * Улучшение порядка: разворот участка (2-opt) и перенос одного места
 * на другую позицию (or-opt), пока это сокращает путь. Путь открытый —
 * начинается в `start`, заканчивается где угодно.
 *
 * Одних разворотов мало: в открытом пути ими не перенести место
 * из конца в начало — а это ровно случай «близкое к старту место
 * оказалось последним». Мест в своём маршруте единицы, полный
 * перебор дешёвый
 */
const twoOpt = (start, list) => {
  let best = list;
  let bestLen = pathLen(start, best);
  const tryCand = (cand) => {
    const len = pathLen(start, cand);
    if (len < bestLen - 1) { best = cand; bestLen = len; return true; }
    return false;
  };
  let improved = true;
  let guard = 0;
  while (improved && guard < 50) {
    improved = false;
    guard += 1;
    for (let i = 0; i < best.length - 1; i++) {
      for (let k = i + 1; k < best.length; k++) {
        if (tryCand([...best.slice(0, i), ...best.slice(i, k + 1).reverse(), ...best.slice(k + 1)])) improved = true;
      }
    }
    for (let i = 0; i < best.length; i++) {
      for (let j = 0; j < best.length; j++) {
        if (i === j) continue;
        const cand = [...best];
        const [item] = cand.splice(i, 1);
        cand.splice(j, 0, item);
        if (tryCand(cand)) improved = true;
      }
    }
  }
  return best;
};

/**
 * Порядок обхода от точки старта: «ближайшее — следующим», затем
 * улучшение 2-opt.
 *
 * Одного жадного прохода мало: на проверке он оставил «Особняк
 * Кшесинской» в 500 м от старта на самый конец, и путь шёл туда
 * и обратно через весь район. Места без координат остаются в конце
 * в прежнем порядке
 *
 * @param {Array<{latitude, longitude}>} stops
 * @param {[number, number] | null} start — откуда идём; нет — от первого места
 */
export const orderByProximity = (stops, start) => {
  const greedy = orderGreedy(stops, start);
  const withPoint = greedy.filter((s) => s.latitude != null && s.longitude != null);
  const without = greedy.filter((s) => s.latitude == null || s.longitude == null);
  // Без старта первое место фиксировано — разворачиваем остальное
  if (!start && withPoint.length > 1) {
    const [first, ...rest] = withPoint;
    return [first, ...twoOpt([first.latitude, first.longitude], rest), ...without];
  }
  return [...twoOpt(start, withPoint), ...without];
};

const orderGreedy = (stops, start) => {
  const withPoint = stops.filter((s) => s.latitude != null && s.longitude != null);
  const without = stops.filter((s) => s.latitude == null || s.longitude == null);
  const remaining = [...withPoint];
  const result = [];
  let cur = start || (remaining[0] ? [remaining[0].latitude, remaining[0].longitude] : null);
  while (remaining.length > 0) {
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const d = calcDistance(cur[0], cur[1], remaining[i].latitude, remaining[i].longitude);
      if (d < bestD) { bestD = d; best = i; }
    }
    const [chosen] = remaining.splice(best, 1);
    result.push(chosen);
    cur = [chosen.latitude, chosen.longitude];
  }
  return [...result, ...without];
};

/** Длина ломаной из мест по прямой, в метрах */
export const straightLength = (points) => points.reduce((sum, p, i) => (
  i ? sum + calcDistance(points[i - 1][0], points[i - 1][1], p[0], p[1]) : 0
), 0);
